import { useCallback, useMemo, useState } from "react";
import { Plus, Trash2 } from "lucide-react";

import {
  ComplianceService,
  type EvidenceFilters,
  type EvidenceListResponse,
  type EvidenceType,
} from "@/api";
import { useApi, useMutation } from "@/hooks/use-api";
import { useAuth } from "@/hooks/use-auth";
import { useTranslation } from "@/i18n";
import type { TranslationKey } from "@/i18n";
import { parseApiError } from "@/lib/api-error";
import { formatDateID } from "@/lib/format";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { FilterChips } from "@/components/shared/filter-chip";
import { Pagination } from "@/components/shared/pagination";
import { ComplianceEvidenceSubmitDialog } from "./compliance-evidence-submit-dialog";

const PAGE_SIZE = 25;
const TENANT_ADMIN_ROLES = new Set(["admin", "super_admin"]);

const EVIDENCE_TYPES = [
  "policy_version",
  "asset",
  "risk_snapshot",
  "audit_log",
  "framework_alignment",
] as const;

// Evidence kinds map to translation keys so the type filter and
// the type column re-render on a locale switch.
const EVIDENCE_TYPE_KEY: Record<EvidenceType, TranslationKey> = {
  policy_version: "compliance.evidenceType.policy_version",
  asset: "compliance.evidenceType.asset",
  risk_snapshot: "compliance.evidenceType.risk_snapshot",
  audit_log: "compliance.evidenceType.audit_log",
  framework_alignment: "compliance.evidenceType.framework_alignment",
};

export function ComplianceEvidenceTab() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const canManage = user ? TENANT_ADMIN_ROLES.has(user.role) : false;

  const typeOptions = useMemo(
    () =>
      EVIDENCE_TYPES.map((type) => ({
        value: type,
        label: t(EVIDENCE_TYPE_KEY[type]),
      })),
    [t]
  );
  const [typeFilter, setTypeFilter] = useState<EvidenceType | null>(null);
  const [includeRevoked, setIncludeRevoked] = useState(false);
  const [page, setPage] = useState(0);
  const [submitOpen, setSubmitOpen] = useState(false);

  const offset = page * PAGE_SIZE;

  const fetcher = useCallback(() => {
    const filters: EvidenceFilters = { limit: PAGE_SIZE, offset };
    if (typeFilter) filters.evidence_type = typeFilter;
    if (includeRevoked) filters.include_revoked = true;
    return ComplianceService.listEvidence(filters);
  }, [typeFilter, includeRevoked, offset]);

  const { data, error, isLoading, refetch } = useApi<EvidenceListResponse>(
    fetcher,
    { deps: [typeFilter, includeRevoked, offset] }
  );

  const revoke = useMutation<string, void>(
    (id) => ComplianceService.revokeEvidence(id, "manual_revocation"),
    { onSuccess: () => void refetch() }
  );

  const items = useMemo(() => data?.items ?? [], [data]);
  const total = data?.total ?? 0;
  const hasNext = offset + items.length < total;

  const handleType = (next: EvidenceType | null) => {
    setTypeFilter(next);
    setPage(0);
  };

  return (
    <div className="space-y-4">
      {error && <Alert variant="warning">{parseApiError(error)}</Alert>}
      {revoke.error && (
        <Alert variant="danger">{parseApiError(revoke.error)}</Alert>
      )}

<div className="flex flex-col gap-3 tablet:flex-row tablet:items-center tablet:justify-between tablet:gap-4">
        <p className="text-sm text-neutral-600">
          {t("compliance.evidence.intro")}
        </p>
        {canManage && (
          <Button onClick={() => setSubmitOpen(true)} size="sm" className="flex-shrink-0 whitespace-nowrap">
            <Plus size={14} />
            {t("compliance.actions.submitEvidence")}
          </Button>
        )}
      </div>

      <div className="flex flex-col gap-2 tablet:flex-row tablet:items-center">
        <FilterChips
          label={t("compliance.evidence.typeFilterLabel")}
          options={typeOptions}
          value={typeFilter}
          onChange={handleType}
        />
        <label className="flex items-center gap-2 text-xs text-neutral-600">
          <input
            type="checkbox"
            checked={includeRevoked}
            onChange={(e) => {
              setIncludeRevoked(e.target.checked);
              setPage(0);
            }}
            className="h-4 w-4 rounded border-ocean-200 text-ocean-600 focus:ring-ocean-400"
          />
          {t("compliance.evidence.includeRevoked")}
        </label>
      </div>

      <div className="overflow-hidden rounded-lg border border-ocean-100 bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("compliance.evidence.colClause")}</TableHead>
              <TableHead>{t("compliance.evidence.colType")}</TableHead>
              <TableHead className="hidden tablet:table-cell">
                {t("compliance.evidence.colReference")}
              </TableHead>
              <TableHead className="hidden tablet:table-cell">
                {t("compliance.evidence.colCreated")}
              </TableHead>
              <TableHead>{t("compliance.evidence.colStatus")}</TableHead>
              <TableHead className="text-right">
                {t("compliance.evidence.colActions")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && items.length === 0 && (
              <>
                {[0, 1, 2, 3].map((i) => (
                  <TableRow key={`ev-sk-${i}`}>
                    <TableCell colSpan={6} className="py-4">
                      <Skeleton className="h-5 w-full" />
                    </TableCell>
                  </TableRow>
                ))}
              </>
            )}
            {!isLoading && items.length === 0 && (
              <TableEmpty
                colSpan={6}
                message={t("compliance.evidence.empty")}
              />
            )}
            {items.map((ev) => (
              <TableRow key={ev.id}>
                <TableCell className="font-mono text-xs text-ocean-700">
                  {ev.clause_id.slice(0, 8)}…
                </TableCell>
                <TableCell className="font-medium text-ocean-900">
                  {EVIDENCE_TYPE_KEY[ev.evidence_type]
                    ? t(EVIDENCE_TYPE_KEY[ev.evidence_type])
                    : ev.evidence_type}
                </TableCell>
                <TableCell className="hidden tablet:table-cell font-mono text-xs text-neutral-600">
                  {ev.reference_id.slice(0, 12)}…
                </TableCell>
                <TableCell className="hidden tablet:table-cell text-neutral-600">
                  {formatDateID(ev.created_at)}
                </TableCell>
                <TableCell>
                  {ev.revoked_at ? (
                    <Badge tone="danger">
                      {t("compliance.evidence.statusRevoked")}
                    </Badge>
                  ) : (
                    <Badge tone="success">
                      {t("compliance.evidence.statusActive")}
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {canManage && !ev.revoked_at && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={revoke.isLoading}
                      onClick={() => void revoke.mutate(ev.id)}
                    >
                      <Trash2 size={14} />
                      {t("compliance.actions.revokeEvidence")}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {items.length > 0 && (
        <Pagination
          page={page}
          pageSize={PAGE_SIZE}
          hasNext={hasNext}
          onPrev={() => setPage((p) => Math.max(0, p - 1))}
          onNext={() => setPage((p) => p + 1)}
        />
      )}

      <ComplianceEvidenceSubmitDialog
        open={submitOpen}
        onClose={() => setSubmitOpen(false)}
        onSuccess={() => {
          setSubmitOpen(false);
          void refetch();
        }}
      />
    </div>
  );
}
